import { useState, useEffect, useRef } from "react";
import { Mic, Square, Keyboard, Loader2, Volume2, Play, Plus, Check, Send, Sparkles, BookOpen, Trash2, Pencil, ArrowLeft, Eye, EyeOff, MessagesSquare, ListChecks } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Seo from "@/components/Seo";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useAudioRecorder } from "@/hooks/useAudioRecorder";
import { Bidi } from "@/lib/bidi";
import { langDisplayName, voiceOK, ttsOK } from "@/lib/languages";
import { LiveFriend } from "@/components/LiveFriend";
import { InterviewPractice, mentionsUpcomingInterview } from "@/components/InterviewPractice";
import { useNavigate, useParams, useLocation } from "react-router-dom";

const SEEN_KEY = "ff_intro_seen";

export default function FluentFriend() {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const targetCode = user?.target_lang || "es";
  const nativeCode = user?.native_lang || "en";
  const targetLabel = langDisplayName(targetCode, locale);

  const [tab, setTab] = useState("chat");              // chat | list
  const [convId, setConvId] = useState(null);
  const [title, setTitle] = useState("");
  const [messages, setMessages] = useState([]);         // {role, text/your_version, meaning}
  const [ivPrefill, setIvPrefill] = useState(null);       // Interview Practice context (learner-confirmed)
  const [ivSuggest, setIvSuggest] = useState(null);
  const [input, setInput] = useState("");
  const [inputMode, setInputMode] = useState("type");
  const [sending, setSending] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [reveal, setReveal] = useState({});             // show-meaning by index
  const [showIntro, setShowIntro] = useState(!localStorage.getItem(SEEN_KEY));
  const [recap, setRecap] = useState(null);
  const [recapLoading, setRecapLoading] = useState(false);
  const [saved, setSaved] = useState({});
  const [conversations, setConversations] = useState([]);
  const navigate = useNavigate();
  const { liveId } = useParams();
  const { pathname, search } = useLocation();
  useEffect(() => {  // 8.3.11: Video Moment → Interview Practice handoff; context stays visible/editable in setup
    const q = new URLSearchParams(search);
    if (q.get("interview") === "1") { setIvPrefill(q.get("context") || ""); setTab("interview"); }
  }, [search]);
  const live = voiceOK(targetCode) && pathname.startsWith("/friend/live") ? (liveId || "new") : null;  // Phase 7.2 routed Live mode
  const exitLive = () => { setTab("list"); if (window.history.state?.idx > 0) navigate(-1); else navigate("/friend", { replace: true }); };

  const { recording, start, stop, audioMime, audioMeta } = useAudioRecorder();
  const audioRef = useRef(null);
  const endRef = useRef(null);
  const recMode = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const stopAudio = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } };

  const loadConversations = () => api.get("/friend/conversations").then((r) => setConversations(r.data.conversations || [])).catch(() => {});
  useEffect(() => { if (tab === "list") loadConversations(); }, [tab]);

  const newChat = () => { setConvId(null); setTitle(""); setMessages([]); setInput(""); setReveal({}); setRecap(null); setSaved({}); setTab("chat"); };

  const openConversation = async (id) => {
    try {
      const r = await api.get(`/friend/conversations/${id}`);
      if (r.data.mode === "live") { navigate(`/friend/live/${r.data.conversation_id}`); return; }
      setConvId(r.data.conversation_id); setTitle(r.data.title);
      setMessages(r.data.messages || []); setReveal({}); setRecap(null); setSaved({}); setTab("chat");
    } catch { toast.error("Couldn't open conversation."); }
  };

  // ---- input: speak -> transcript lands in editable input (review before send) ----
  const toggleRecord = async () => {
    stopAudio();
    if (recording) {
      const b64 = await stop(); recMode.current = null;
      if (!b64) { toast.error(t("friend.micDenied")); setInputMode("type"); return; }
      setTranscribing(true);
      try {
        const r = await api.post("/friend/transcribe", { audio: b64, mime: audioMime.current, meta: audioMeta.current });
        setInput((prev) => (prev ? prev + " " : "") + r.data.transcript);
      } catch (e) {
        if (e?.response?.status === 402) toast.error("Fluent Friend is a premium feature.");
        else toast.error(e?.response?.data?.detail || t("friend.micDenied"));
        setInputMode("type");
      } finally { setTranscribing(false); }
      return;
    }
    try { recMode.current = "input"; await start(); }
    catch { recMode.current = null; toast.error(t("friend.micDenied")); setInputMode("type"); }
  };

  const send = async (textArg) => {
    const native_text = (textArg ?? input).trim();
    if (!native_text || sending) return;
    setSending(true); setRecap(null);
    const mode = inputMode === "speak" ? "voice" : "text";
    try {
      const r = await api.post("/friend/turn", { conversation_id: convId, native_text, interaction_mode: mode });
      setConvId(r.data.conversation_id); setTitle(r.data.title);
      setMessages((m) => [...m,
        { role: "learner", your_version: r.data.your_version, meaning: r.data.your_version_meaning, native: native_text },
        { role: "friend", text: r.data.friend_reply, meaning: r.data.friend_reply_meaning },
      ]);
      setInput("");
      if (mentionsUpcomingInterview(native_text)) setIvSuggest(native_text);
    } catch (e) {
      if (e?.response?.status === 402) toast.error("Fluent Friend is a premium feature.");
      else toast.error(e?.response?.data?.detail || "Fluent Friend couldn't reply — your message is safe, try again.");
    } finally { setSending(false); }
  };

  const listen = async (text) => {
    if (!text) return;
    stopAudio();
    try {
      const r = await api.post("/friend/tts", { text });
      const audio = new Audio(`data:audio/mp3;base64,${r.data.audio}`);
      audioRef.current = audio;
      await audio.play();
    } catch { toast.error("Couldn't play audio."); }
  };

  const loadRecap = async () => {
    if (!convId) return;
    setRecapLoading(true);
    try { const r = await api.post(`/friend/conversations/${convId}/recap`); setRecap(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't build the recap."); }
    finally { setRecapLoading(false); }
  };

  const addVocab = async (items) => {
    const fresh = (items || []).filter((v) => (v.word || v.phrase || v.expression) && !saved[v.word || v.phrase || v.expression]);
    if (!fresh.length) return;
    try {
      await api.post("/vocab/add", {
        items: fresh.map((v) => ({ word: v.word || v.phrase || v.expression, translation: v.translation, example_sentence: "" })),
        source: "fluent_friend",
      });
      setSaved((s) => { const n = { ...s }; fresh.forEach((v) => { n[v.word || v.phrase || v.expression] = true; }); return n; });
      toast.success(`${t("common.added")}: ${fresh.length}`);
    } catch { toast.error("Couldn't add to Vocab Bank."); }
  };

  const renameConv = async (c) => {
    const nt = window.prompt(t("common.rename") || "Rename", c.title || "");
    if (nt === null) return;
    try { await api.patch(`/friend/conversations/${c.conversation_id}`, { title: nt }); if (c.conversation_id === convId) setTitle(nt); loadConversations(); }
    catch { toast.error("Couldn't rename."); }
  };
  const deleteConv = async (id) => {
    try { await api.delete(`/friend/conversations/${id}`); if (id === convId) newChat(); loadConversations(); toast.success(t("journal.deleted")); }
    catch { toast.error("Couldn't delete."); }
  };

  const Support = ({ idx, meaning, code }) => (
    meaning ? (
      <>
        <button onClick={() => setReveal((r) => ({ ...r, [idx]: !r[idx] }))} className="text-xs font-bold text-gray-500 inline-flex items-center gap-1 mt-1" data-testid="friend-show-meaning">
          {reveal[idx] ? <><EyeOff className="w-3 h-3" /> {t("friend.hideMeaning")}</> : <><Eye className="w-3 h-3" /> {t("friend.showMeaning")}</>}
        </button>
        {reveal[idx] && <Bidi code={code} as="div" className="text-xs text-gray-500 mt-0.5">{meaning}</Bidi>}
      </>
    ) : null
  );

  const RecapList = ({ items, label, keyField }) => (
    (items && items.length) ? (
      <div className="mt-3">
        <div className="flex items-center justify-between mb-1">
          <h5 className="font-cabinet font-bold text-xs uppercase tracking-wide text-gray-600">{label}</h5>
          <button onClick={() => addVocab(items)} className="nb-btn-outline py-0.5 px-2 text-xs" data-testid="friend-recap-addall"><Plus className="w-3 h-3" /> {t("common.addAll")}</button>
        </div>
        {items.map((v, i) => {
          const term = v[keyField];
          return (
            <div key={i} className="flex items-center justify-between gap-2 border-b border-gray-100 py-1" data-testid="friend-recap-row">
              <div className="min-w-0"><Bidi code={targetCode} className="font-bold text-sm">{term}</Bidi>{v.translation && <Bidi code={nativeCode} as="span" className="text-xs text-gray-500"> · {v.translation}</Bidi>}</div>
              <button onClick={() => addVocab([v])} disabled={!!saved[term]} className={`shrink-0 py-0.5 px-2 text-xs rounded-lg border-2 border-ink font-bold ${saved[term] ? "bg-mint" : "bg-butter"}`} data-testid="friend-recap-add">
                {saved[term] ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
              </button>
            </div>
          );
        })}
      </div>
    ) : null
  );

  return (
    <AppLayout>
      <Seo title="Fluent Friend · FluentLoop" />
      <div className="max-w-3xl mx-auto px-3 md:px-0" data-testid="fluent-friend">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <h1 className="font-cabinet font-extrabold text-3xl flex items-center gap-2"><MessagesSquare className="w-7 h-7 text-coral" /> {t("friend.title")}</h1>
          <div className="flex items-center gap-1 bg-paper border-2 border-ink rounded-xl p-0.5">
            <button onClick={newChat} className={`px-3 py-1.5 rounded-lg text-sm font-bold ${tab === "chat" ? "bg-ink text-white" : ""}`} data-testid="friend-tab-chat">{t("iv.tabNew")}</button>
            <button onClick={() => setTab("list")} className={`px-3 py-1.5 rounded-lg text-sm font-bold ${tab === "list" ? "bg-ink text-white" : ""}`} data-testid="friend-tab-list">{t("iv.tabChats")}</button>
            <button onClick={() => { setIvPrefill(null); setTab("interview"); if (live) navigate("/friend"); }} className={`px-3 py-1.5 rounded-lg text-sm font-bold ${tab === "interview" ? "bg-ink text-white" : ""}`} data-testid="friend-tab-interview">{t("iv.tabInterview")}</button>
          </div>
        </div>
        <p className="text-gray-600 text-sm mb-4">{t("friend.tagline")}</p>

        {!live && tab === "interview" && <InterviewPractice targetCode={targetCode} script={user?.target_script} prefill={ivPrefill} onExit={() => setTab("chat")} />}

        {!live && tab === "chat" && messages.length === 0 && (
          <button onClick={() => { setIvPrefill(null); setTab("interview"); }} className="nb-card w-full p-4 bg-lavender mb-4 text-start" data-testid="friend-interview-card">
            <div className="font-cabinet font-bold">{t("iv.cardTitle")}</div>
            <div className="text-sm text-gray-700">{t("iv.cardSub")}</div>
          </button>
        )}

        {!live && tab === "chat" && ivSuggest && (
          <div className="flex flex-wrap items-center gap-2 text-sm mb-3 border-2 border-dashed border-ink rounded-xl px-3 py-2" data-testid="friend-interview-suggest">
            <span>{t("iv.suggest")}</span>
            <button onClick={() => { setIvPrefill(ivSuggest); setIvSuggest(null); setTab("interview"); }} className="font-bold underline" data-testid="friend-interview-suggest-open">{t("iv.suggestBtn")}</button>
            <button onClick={() => setIvSuggest(null)} className="text-gray-500 ms-auto" data-testid="friend-interview-suggest-dismiss">{t("iv.dismiss")}</button>
          </div>
        )}

        {live && <LiveFriend conversationId={live === "new" ? null : live} targetCode={targetCode} nativeCode={nativeCode} onExit={exitLive} onConversationId={(id) => navigate(`/friend/live/${id}`, { replace: true })} />}

        {!live && tab === "chat" && voiceOK(targetCode) && (
          <button onClick={() => navigate("/friend/live")} className="nb-btn-primary w-full py-3 mb-4" data-testid="friend-start-live">
            <Mic className="w-5 h-5" /> {t("friend.live.start")}
          </button>
        )}

        {/* ---------------- CHAT ---------------- */}
        {!live && tab === "chat" && (
          <>
            {showIntro && (
              <div className="nb-card p-4 bg-sky mb-4 flex items-start justify-between gap-3" data-testid="friend-intro">
                <p className="text-sm">{t(voiceOK(targetCode) ? "friend.intro" : "friend.introText", { lang: targetLabel })}</p>
                <button onClick={() => { localStorage.setItem(SEEN_KEY, "1"); setShowIntro(false); }} className="shrink-0 text-xs font-bold underline" data-testid="friend-intro-dismiss">OK</button>
              </div>
            )}

            {messages.length === 0 && !sending && (
              <div className="nb-card p-5 mb-4" data-testid="friend-starters">
                <h4 className="font-cabinet font-bold text-sm mb-2">{t("friend.startersTitle")}</h4>
                <div className="flex flex-wrap gap-2">
                  {(t("friend.starters") || []).map((s, i) => (
                    <button key={i} onClick={() => send(s)} className="nb-tag bg-butter hover:-translate-y-0.5 transition-transform text-sm" data-testid={`friend-starter-${i}`}>{s}</button>
                  ))}
                </div>
              </div>
            )}

            {/* messages */}
            <div className="space-y-3 mb-4">
              {messages.map((m, i) => (
                m.role === "learner" ? (
                  <div key={i} className="ml-auto max-w-[85%]" data-testid="friend-learner-msg">
                    <div className="nb-card p-3 bg-mint">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-gray-600">{t("friend.youCouldSay")}</div>
                      <Bidi code={targetCode} as="p" className="font-medium mt-0.5">{m.your_version}</Bidi>
                      <div className="flex items-center gap-2 mt-1.5">
                        {ttsOK(targetCode) && <button onClick={() => listen(m.your_version)} className="text-xs font-bold text-gray-600 inline-flex items-center gap-1" data-testid="friend-listen"><Volume2 className="w-3 h-3" /> {t("common.listen")}</button>}
                      </div>
                      <Support idx={i} meaning={m.meaning} code={nativeCode} />
                    </div>
                  </div>
                ) : (
                  <div key={i} className="mr-auto max-w-[85%]" data-testid="friend-reply-msg">
                    <div className="nb-card p-3 bg-white">
                      <Bidi code={targetCode} as="p">{m.text}</Bidi>
                      <div className="flex items-center gap-2 mt-1.5">
                        {ttsOK(targetCode) && <button onClick={() => listen(m.text)} className="text-xs font-bold text-gray-600 inline-flex items-center gap-1" data-testid="friend-reply-listen"><Volume2 className="w-3 h-3" /> {t("common.listen")}</button>}
                      </div>
                      <Support idx={i} meaning={m.meaning} code={nativeCode} />
                    </div>
                  </div>
                )
              ))}
              {sending && <div className="mr-auto nb-card p-3 bg-white text-sm text-gray-500" data-testid="friend-thinking"><Loader2 className="w-4 h-4 inline animate-spin" /> {t("friend.thinking")}</div>}
              <div ref={endRef} />
            </div>

            {/* recap */}
            {messages.length > 0 && (
              <div className="mb-4">
                {!recap ? (
                  <button onClick={loadRecap} disabled={recapLoading} className="nb-btn-outline py-2 px-4 text-sm" data-testid="friend-recap-btn">
                    {recapLoading ? <><Loader2 className="w-4 h-4 inline animate-spin" /> …</> : <><ListChecks className="w-4 h-4 inline" /> {t("friend.recap")}</>}
                  </button>
                ) : (
                  <div className="nb-card p-4 bg-paper" data-testid="friend-recap">
                    <h4 className="font-cabinet font-bold text-sm uppercase tracking-wide text-gray-700"><BookOpen className="w-4 h-4 inline" /> {t("friend.recap")}</h4>
                    <RecapList items={recap.words} label={t("friend.words")} keyField="word" />
                    <RecapList items={recap.phrases} label={t("friend.phrases")} keyField="phrase" />
                    <RecapList items={recap.expressions} label={t("friend.expressions")} keyField="expression" />
                    {recap.grammar?.length > 0 && (
                      <div className="mt-3">
                        <h5 className="font-cabinet font-bold text-xs uppercase tracking-wide text-gray-600 mb-1">{t("friend.grammar")}</h5>
                        {recap.grammar.map((g, i) => <div key={i} className="mb-1"><span className="font-bold text-sm">{g.point}</span> <Bidi code={nativeCode} as="span" className="text-xs text-gray-500">— {g.explanation}</Bidi></div>)}
                      </div>
                    )}
                    {recap.practice?.length > 0 && (
                      <div className="mt-3">
                        <h5 className="font-cabinet font-bold text-xs uppercase tracking-wide text-gray-600 mb-1">{t("friend.practiceAgain")}</h5>
                        {recap.practice.map((p, i) => (
                          <div key={i} className="flex items-center gap-2 mb-1"><Bidi code={targetCode} className="text-sm">{p}</Bidi><button onClick={() => listen(p)} className="text-gray-500" data-testid="friend-practice-listen"><Volume2 className="w-3 h-3" /></button></div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* input bar */}
            <div className="sticky bottom-2 nb-card p-3 bg-white" data-testid="friend-input-bar">
              <div className="flex items-center gap-2 mb-2">
                {voiceOK(targetCode) && <button onClick={() => setInputMode("speak")} className={`py-1 px-3 rounded-lg border-2 border-ink text-xs font-bold ${inputMode === "speak" ? "bg-mint" : "bg-white"}`} data-testid="friend-mode-speak"><Mic className="w-3 h-3 inline" /> {t("friend.speak")}</button>}
                <button onClick={() => setInputMode("type")} className={`py-1 px-3 rounded-lg border-2 border-ink text-xs font-bold ${inputMode === "type" ? "bg-mint" : "bg-white"}`} data-testid="friend-mode-type"><Keyboard className="w-3 h-3 inline" /> {t("friend.type")}</button>
                {input && <span className="text-xs text-gray-400">{t("friend.reviewHint")}</span>}
              </div>
              <div className="flex items-end gap-2">
                {inputMode === "speak" && (
                  <button onClick={toggleRecord} disabled={transcribing} className={`shrink-0 w-11 h-11 rounded-full border-2 border-ink flex items-center justify-center ${recording ? "bg-coral animate-pulse" : "bg-butter"}`} data-testid="friend-record-btn">
                    {transcribing ? <Loader2 className="w-5 h-5 animate-spin" /> : recording ? <Square className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                  </button>
                )}
                <textarea value={input} onChange={(e) => setInput(e.target.value)} dir="auto" rows={1}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder={recording ? t("friend.recording") : t("friend.placeholder")} className="nb-input flex-1 resize-none" data-testid="friend-input" />
                <button onClick={() => send()} disabled={sending || !input.trim()} className="shrink-0 nb-btn-primary w-11 h-11 p-0 flex items-center justify-center" data-testid="friend-send"><Send className="w-5 h-5" /></button>
              </div>
            </div>
          </>
        )}

        {/* ---------------- LIST ---------------- */}
        {!live && tab === "list" && (
          <div className="space-y-2" data-testid="friend-list">
            {conversations.length === 0 && <div className="nb-card p-8 text-center text-gray-500">{t("friend.empty")}</div>}
            {conversations.map((c) => (
              <div key={c.conversation_id} className="nb-card p-4 flex items-center justify-between gap-2" data-testid={`friend-conv-${c.conversation_id}`}>
                <button onClick={() => openConversation(c.conversation_id)} className="min-w-0 text-start flex-1">
                  <div className="font-cabinet font-bold truncate">{c.mode === "live" && <span className="nb-tag bg-coral text-white text-[10px] me-1.5" data-testid="friend-live-tag">{t("friend.live.tag")}</span>}{c.title}</div>
                  <div className="text-xs text-gray-400" data-testid="friend-conv-langs">{langDisplayName(c.native_language, locale)} {locale === "ar" ? "←" : "→"} {langDisplayName(c.target_language, locale)} · {c.level}</div>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => renameConv(c)} className="nb-btn-outline py-1.5 px-2 text-xs" data-testid="friend-rename"><Pencil className="w-3 h-3" /></button>
                  <button onClick={() => deleteConv(c.conversation_id)} className="nb-btn-outline py-1.5 px-2 text-xs" data-testid="friend-delete"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
